import { Box, SxProps, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useExportCampaignDataLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import {
  Campaign,
  CampaignStatus,
  CampaignType
} from 'services/campaign/types';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';
import { userAtom } from 'services/user/atoms';
import { getDocumentTypesFromQuestions } from 'pages/CampaignAutomationPage/utils/getDocumentTypesFromQuestions';
import { AtsJobLink } from 'services/integration/components/AtsJobLink';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { Button } from 'system/atoms';
import { usePortalUrl } from 'modules/portal/hooks/usePortalUrl';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { StopCampaignDrawer } from 'modules/campaigns/components/StopCampaignDrawer';
import { STOPPABLE_CAMPAIGN_STATUSES } from 'services/campaign/constants';
import { WorkflowBuilderIcon } from 'common/icons/WorkflowBuilderIcon';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';

const actionHeaderContainer: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 2
};

type Props = {
  campaign: Campaign;
  openSubscribersModal: () => void;
  openViewQuestions: () => void;
  openEditExternalId: () => void;
  openEditCampaignDetails: () => void;
  onLaunchCampaign?: () => void;
  onLaunchNow?: () => void;
  onRescheduleLaunch?: () => void;
  isLaunching?: boolean;
};

const ActionHeader: React.FC<Props> = ({
  campaign,
  openSubscribersModal,
  openViewQuestions,
  openEditExternalId,
  openEditCampaignDetails,
  onLaunchCampaign,
  onLaunchNow,
  onRescheduleLaunch,
  isLaunching = false
}) => {
  const navigate = useNavigate();
  const isEditExternalIdEnabledFlag = useFlag(Flags.EDIT_EXTERNAL_ID);

  const { showToast } = useToast();
  const user = useRecoilValue(userAtom);
  const { track } = useTracking();
  const { openDrawer } = useDrawerV2();
  const [exportCampaignData, { loading: isExporting, data: exportData }] =
    useExportCampaignDataLazyQuery({
      fetchPolicy: 'network-only'
    });
  const hasExported = !!exportData?.exportCampaignData?.message;

  const isCampaignOwner = campaign.userId === user?.id;
  const isAdmin = user?.id;
  const isCampaignConnectedToAts = !!(
    campaign.atsJobId ||
    campaign.atsListId ||
    campaign.atsJobPostingId
  );

  const { isGeneratingPortalUrl, onCopyPortalUrl, canCopyPortalUrl } =
    usePortalUrl({
      campaignId: campaign.id
    });

  const onExportData = async () => {
    try {
      track(TrackingEventName.CampaignDashboardActionExport);
      const { data, error } = await exportCampaignData({
        variables: { campaignId: campaign.id }
      });
      if (data?.exportCampaignData && !error) {
        showToast({
          message:
            'Data exported successfully. You will receive an email with the download link.',
          type: ToastType.Success
        });
      } else if (error) {
        showToast({
          message: 'Could not export data',
          type: ToastType.Error
        });
      } else {
        showToast({
          message: 'Could not export data',
          type: ToastType.Error
        });
      }
    } catch {
      showToast({
        message: 'Could not export data',
        type: ToastType.Error
      });
    }
  };

  const shouldShowAutomation = useMemo(() => {
    const documentQuestions = getDocumentTypesFromQuestions(
      campaign.questions || []
    );

    return documentQuestions.length > 0 || isCampaignConnectedToAts;
  }, [campaign.questions?.length, isCampaignConnectedToAts]);

  const isReadyToLaunch =
    campaign.status === CampaignStatus.FINISHED_GENERATING &&
    !!campaign.totalProspectsCount;

  const isScheduled = campaign.status === CampaignStatus.SCHEDULED;

  const isArchived = campaign.status === CampaignStatus.ARCHIVED;
  const isGenerating =
    campaign.status === CampaignStatus.GENERATING_MESSAGES ||
    campaign.status === CampaignStatus.PROCESSING;

  const isStoppable = STOPPABLE_CAMPAIGN_STATUSES.includes(campaign.status);

  const onStopCampaignClick = () => {
    openDrawer({
      title: 'Stop campaign',
      width: '450px',
      children: (
        <StopCampaignDrawer
          campaign={campaign}
          onCompleted={() => navigate(Paths.CAMPAIGNS)}
        />
      )
    });
  };

  const actionOptions = useMemo<ActionOption[]>(() => {
    const options: ActionOption[] = [];

    if (!isArchived && isScheduled && onRescheduleLaunch) {
      options.push({
        label: 'Reschedule launch',
        onClick: onRescheduleLaunch
      });
    }

    if (!campaign.workflowId) {
      options.push({
        label: 'View questions',
        onClick: openViewQuestions
      });
    }

    // Workflow-linked campaigns don't support Typeform triggers, so the
    // Typeform link would route candidates into the campaign rather than the
    // workflow. Hide it for workflow-linked campaigns.
    if (!isArchived && canCopyPortalUrl && !campaign.workflowId) {
      options.push({
        label: 'Copy Typeform link',
        loadingLabel: 'Copying',
        isLoading: isGeneratingPortalUrl,
        onClick: onCopyPortalUrl
      });
    }

    if (!isArchived && (isCampaignOwner || isAdmin)) {
      options.push({
        label: 'Notify team members',
        onClick: () => {
          track(TrackingEventName.CampaignDashboardActionNotifyTeam);
          openSubscribersModal();
        }
      });
    }

    options.push({
      label: hasExported ? 'Export sent' : 'Export campaign data',
      loadingLabel: 'Exporting',
      isLoading: isExporting,
      disabled: hasExported,
      onClick: onExportData
    });

    if (!isArchived && shouldShowAutomation && !campaign.workflowId) {
      options.push({
        label: 'Automations',
        onClick: () => {
          track(TrackingEventName.CampaignDashboardAutomation);
          navigate(`${Paths.CAMPAIGN_AUTOMATION}/${campaign.id}`);
        }
      });
    }

    if (isEditExternalIdEnabledFlag) {
      options.push({
        label: 'Edit external ID',
        onClick: openEditExternalId
      });
    }

    if (isStoppable) {
      options.push({
        label: 'Stop campaign',
        onClick: onStopCampaignClick,
        itemColor: 'error.main'
      });
    }

    return options;
  }, [
    openViewQuestions,
    isEditExternalIdEnabledFlag,
    openEditExternalId,
    shouldShowAutomation,
    track,
    navigate,
    campaign.id,
    onCopyPortalUrl,
    isGeneratingPortalUrl,
    canCopyPortalUrl,
    isCampaignOwner,
    isAdmin,
    openSubscribersModal,
    isStoppable,
    onStopCampaignClick,
    onExportData,
    isExporting,
    hasExported,
    onRescheduleLaunch,
    isScheduled,
    isArchived
  ]);

  return (
    <Box sx={actionHeaderContainer}>
      {!isArchived && isReadyToLaunch && onLaunchCampaign && (
        <Button variant="tertiary" onClick={onLaunchCampaign}>
          Launch campaign
        </Button>
      )}
      {!isArchived && isScheduled && onLaunchNow && (
        <Button variant="tertiary" onClick={onLaunchNow} disabled={isLaunching}>
          {isLaunching ? 'Launching...' : 'Launch now'}
        </Button>
      )}
      {campaign.workflowId && (
        <Button
          variant="tertiary"
          onClick={() =>
            window.open(
              `${workflowBuilderLink}/${campaign.workflowId}`,
              '_blank'
            )
          }
          startIcon={<WorkflowBuilderIcon size={14} />}
        >
          View in workflow
        </Button>
      )}
      {!isArchived && (
        <Button variant="secondary" onClick={openEditCampaignDetails}>
          {campaign.type === CampaignType.ENGAGEMENT_OUTREACH
            ? 'Edit campaign details'
            : 'Edit job details'}
        </Button>
      )}
      {!isArchived && !isCampaignConnectedToAts && !campaign.workflowId && (
        <Tooltip
          title={
            isGenerating
              ? 'Candidates cannot be added while messages are being generated'
              : ''
          }
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="primary"
              disabled={isGenerating}
              onClick={() => {
                track(TrackingEventName.CampaignDashboardAddCandidates);
                navigate(`${Paths.CAMPAIGN_ADD_CSV_CANDIDATES}/${campaign.id}`);
              }}
            >
              Add candidates
            </Button>
          </span>
        </Tooltip>
      )}
      {!isArchived && isCampaignConnectedToAts && (
        <AtsJobLink atsJobId={campaign.atsJobId} />
      )}

      <ActionPopover id={`campaign-${campaign.id}`} options={actionOptions} />
    </Box>
  );
};

export { ActionHeader };
